import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type CustomQueryRecord from 'webapp/models/custom-query';

export default class CustomQuerySerializer extends ApplicationSerializer {
	override getReadOnlyFields(
		_json: Record<string, unknown>,
	): RecordData.CustomQuery.ReadOnlyKey[] {
		return [
			'createdById',
			'statusLevel',
			'statusTitle',
			'statusDescription',
			'statusAction',
			'statusUserResponseLink',
			'cloneFrom',
		];
	}

	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.CustomQuery.Raw | RecordData.CustomQuery.Serialized,
		prop: string | undefined,
	) {
		const attributes: Partial<RecordAttributes<CustomQueryRecord>> & { id?: string | number } =
			omit(json as RecordData.CustomQuery.Raw, 'statusObject', 'createdBy');

		if ('statusObject' in json && json.statusObject) {
			const { statusObject } = json;
			attributes.statusLevel = statusObject.level;
			attributes.statusTitle = statusObject.title;
			attributes.statusDescription = statusObject.description;
			attributes.statusAction = statusObject.userAction;
			attributes.statusUserResponseLink = statusObject.userResponseLink;
			attributes.statusType = statusObject.type;
		}

		if ('createdBy' in json && json.createdBy) {
			attributes.createdById = json.createdBy;
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.CustomQuery.Serialized {
		// read-only fields are already removed by ApplicationSerializer
		const attributes = super.serialize(snapshot, options) as Omit<
			RecordAttributes<CustomQueryRecord>,
			RecordData.CustomQuery.ReadOnlyKey
		>;

		const omittedKeys: (
			'verificationInfo' | 'settings' | 'transformations' | 'defaultTitle'
		)[] = ['verificationInfo'];

		if (
			Object.keys(attributes.settings).length === 0 &&
			Object.keys(attributes.transformations).length === 0
		) {
			// assuming that we're just updating tags
			omittedKeys.push('settings', 'transformations');
		}

		if (attributes.defaultTitle && Object.keys(attributes.defaultTitle).length === 0) {
			omittedKeys.push('defaultTitle');
		}

		return omit(attributes, ...omittedKeys);
	}
}
